import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { IMG, TRUST } from "../../data/content";

const lines = ["Cleaner Spaces.", "Healthier Environments.", "Greener Properties."];

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => (
  <section data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden bg-sml-forest">
    <motion.div
      initial={{ scale: 1.12 }}
      animate={{ scale: 1 }}
      transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0"
    >
      <img src={IMG.heroBuilding} alt="Modern corporate building with clean landscaping" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-sml-forest via-sml-forest/70 to-sml-forest/30" />
      <div className="absolute inset-0 bg-sml-forest/20" />
    </motion.div>

    <div className="relative mx-auto max-w-7xl w-full px-5 lg:px-8 pt-32 pb-14 lg:pb-20">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      >
        <span className="overline text-sml-bronze inline-flex items-center gap-2">
          <span className="h-px w-8 bg-sml-bronze" />
          Property Support Services You Can Trust
        </span>
      </motion.div>

      <motion.h1
        variants={lineParent}
        initial="hidden"
        animate="show"
        className="mt-6 font-display font-medium text-white text-[2.7rem] leading-[1.02] sm:text-6xl lg:text-7xl tracking-tight max-w-5xl"
      >
        {lines.map((l, i) => (
          <span key={i} className="reveal-line">
            <motion.span variants={lineChild} className="block">
              {i === 2 ? <span className="text-sml-bronze">{l}</span> : l}
            </motion.span>
          </span>
        ))}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.7 }}
        className="mt-8 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed"
      >
        SML Property Solutions provides professional horticultural, cleaning, hygiene and facility
        support services designed to keep commercial, industrial, residential and public-sector
        environments clean, healthy, safe and well maintained.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.85, duration: 0.7 }}
        className="mt-10 flex flex-wrap gap-4"
      >
        <Link
          to="/contact"
          data-testid="hero-proposal-button"
          className="group inline-flex items-center gap-2 rounded-sm bg-sml-bronze text-sml-forest font-semibold px-7 py-4 hover:bg-white transition-colors active:scale-[0.98]"
        >
          Request a Service Proposal
          <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <Link
          to="/services"
          data-testid="hero-services-button"
          className="group inline-flex items-center gap-2 rounded-sm border border-white/30 text-white font-medium px-7 py-4 hover:bg-white/10 transition-colors active:scale-[0.98]"
        >
          Explore Our Services
          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </Link>
      </motion.div>
    </div>

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1, duration: 0.8 }}
      className="relative border-t border-white/15 bg-sml-forest/40 backdrop-blur-md"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
        {TRUST.map((t) => (
          <div key={t.label} className="py-5 px-4 first:pl-0">
            <div className="font-mono-sml text-[10px] uppercase tracking-[0.2em] text-sml-bronze">{t.label}</div>
            <div className="mt-1 font-display text-white text-lg md:text-xl">{t.value}</div>
          </div>
        ))}
      </div>
    </motion.div>
  </section>
);
